import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, EyeOff, Check, Truck, Shirt } from "lucide-react";
import { api, eur, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Loader, PriceBreakdown, InsuranceBadge } from "@/components/common";

const STEPS = ["book.step1", "book.step2", "book.step3", "book.step4", "book.step5"];
const tomorrow = () => new Date(Date.now() + 864e5).toISOString().slice(0, 10);
const emptyDraft = () => ({ kg: 6, extras: { dryer: false, folding: false, pieces: [0, 0, 0], iron_flat: false, transport: "none" }, heavy_soil: false, heavy_soil_desc: "", delicate: null, delicate_types: [], delicate_notes: "", delicate_label_photos: [], date: tomorrow(), slot: "", self_pickup: false, address: { street: "", plz: "", city: "", notes: "" } });
const loadDraft = (key) => {
  const own = JSON.parse(sessionStorage.getItem(key) || "null");
  if (own) return own;
  const calc = JSON.parse(sessionStorage.getItem("washly_draft_calc") || "null");
  sessionStorage.removeItem("washly_draft_calc");
  return calc ? { ...emptyDraft(), ...calc } : emptyDraft();
};
const TRANSPORT_MODES = ["none", "pickup", "return", "both"];

function Stepper({ step, setStep }) {
  const { t } = useT();
  return (
    <ol className="grid grid-cols-5 gap-2 mb-6" aria-label="Buchungsschritte">
      {STEPS.map((s, i) => (
        <li key={s}>
          <button type="button" disabled={i + 1 > step} onClick={() => setStep(i + 1)} aria-current={i + 1 === step ? "step" : undefined} data-testid={`stepper-step-${i + 1}`}
            className="w-full text-left disabled:cursor-default">
            <span className="block h-1.5 rounded-full mb-2" style={{ background: i + 1 <= step ? "var(--w-mint)" : "var(--w-line)" }} />
            <span className={`text-sm font-semibold hidden sm:inline ${i + 1 === step ? "" : "w-muted"}`}>{i + 1 < step ? "✓ " : `${i + 1}. `}{t(s)}</span>
            <span className="sm:hidden text-sm font-semibold">{i + 1}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

function Toggle({ checked, onChange, label, price, testId, disabled }) {
  return (
    <label className={`w-card p-4 flex items-center justify-between gap-3 cursor-pointer ${checked ? "!border-[var(--w-mint-ink)] bg-mint-soft" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <span className="flex items-center gap-3">
        <input type="checkbox" className="h-5 w-5 accent-[var(--w-mint-ink)]" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
        <span className="font-semibold">{label}</span>
      </span>
      <span className="tabular-nums w-muted">{price}</span>
    </label>
  );
}

function TransportStep({ d, set, p, quote }) {
  const { t } = useT();
  const s = p.services || {};
  const cfg = p.pricing_config || { transport_free_km: 2, km_rate_one_way: 1, km_rate_both: 2, transport_fee: 8.9 };
  const FEE = cfg.transport_fee ?? 8.9;
  const mode = d.self_pickup ? "none" : (d.extras.transport === true ? "both" : d.extras.transport || "none");
  const choose = (m) => set({ self_pickup: m === "none", extras: { ...d.extras, transport: m } });
  const allowed = { none: true, pickup: !!s.pickup, return: !!s.return, both: !!(s.pickup && s.return) };
  const modes = TRANSPORT_MODES.filter((m) => allowed[m]);
  const feeFor = (m) => (m === "none" ? "0 €" : m === "both" ? `+${eur(FEE)}` : `+${eur(Math.round(FEE * 55) / 100)}`);
  const distLine = quote?.lines?.find((l) => l.key === "distance");
  return (
    <div className="space-y-3">
      <p className="font-bold">{t("transport.title")}</p>
      <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label={t("transport.title")}>
        {modes.map((m) => (
          <button type="button" key={m} role="radio" aria-checked={mode === m} onClick={() => choose(m)} data-testid={`transport-${m}`}
            className={`w-card p-4 text-left flex items-center justify-between gap-3 ${mode === m ? "!border-[var(--w-mint-ink)] bg-mint-soft" : ""}`}>
            <span className="flex items-center gap-3"><Truck size={18} className="text-mint-ink" aria-hidden="true" /><span className="font-semibold">{t(`transport.${m}`)}</span></span>
            <span className="tabular-nums w-muted text-sm">{feeFor(m)}</span>
          </button>
        ))}
      </div>
      {!s.pickup && !s.return && <p className="w-muted text-sm">Dieser Waschheld bietet keinen Hol- & Bringservice an.</p>}
      {mode === "none" && <p className="w-muted text-sm">{t("book.selfpickup_hint")}</p>}
      {mode !== "none" && (
        <p className="w-muted text-sm" data-testid="transport-distance-info">
          {quote?.distance_km != null ? `${t("transport.distance")}: ${String(quote.distance_km).replace(".", ",")} km${distLine ? ` · ${t("line.distance")} ${eur(distLine.total)}` : " · inklusive"}` : t("transport.dist_later")}
          {" "}{t("transport.dist_hint").replace("{km}", cfg.transport_free_km).replace("{a}", cfg.km_rate_one_way).replace("{b}", cfg.km_rate_both)}
        </p>
      )}
    </div>
  );
}

const CONSUMABLE_LABELS = { softener: "Weichspüler", perfume: "Wäscheparfum", sensitive: "Sensitiv-Waschmittel" };

function ExtrasStep({ d, setX, set, p, quote }) {
  const { t } = useT();
  const s = p.services || {};
  const pc = p.pricing_config || {};
  const DRYER = pc.dryer_per_kg ?? 0.39, FOLD = pc.folding_per_kg ?? 0.69, CONS = pc.consumable_per_kg ?? 0.19;
  const [upBusy, setUpBusy] = useState(false);
  const DTYPES = [["seide", "Seide"], ["kaschmir", "Kaschmir"], ["wolle", "Wolle"], ["andere", "Andere empfindliche Textilien"]];
  const toggleType = (k) => { const cur = d.delicate_types || []; set({ delicate_types: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] }); };
  const uploadLabel = async (file) => {
    if (!file) return;
    setUpBusy(true);
    try { const { data } = await api.post("/uploads/care-label", (() => { const fd = new FormData(); fd.append("file", file); return fd; })()); set({ delicate_label_photos: [...(d.delicate_label_photos || []), data.file_id] }); toast.success("Etikettenfoto hinzugefügt"); }
    catch (e) { toast.error(errMsg(e)); } finally { setUpBusy(false); }
  };
  return (
    <div className="space-y-3">
      <fieldset className="w-card p-4 space-y-3 border-mint" data-testid="delicate-block">
        <legend className="w-label px-1">Enthält deine Wäsche empfindliche Textilien? (Pflicht)</legend>
        <div className="grid sm:grid-cols-2 gap-2">
          <label className={`w-card p-3 flex gap-2 items-center cursor-pointer ${d.delicate === false ? "!border-[var(--w-mint-ink)] bg-mint-soft" : ""}`}>
            <input type="radio" name="delicate" checked={d.delicate === false} onChange={() => set({ delicate: false, delicate_types: [], delicate_notes: "", delicate_label_photos: [] })} data-testid="delicate-no" /> Nein, keine empfindlichen Textilien enthalten.
          </label>
          <label className={`w-card p-3 flex gap-2 items-center cursor-pointer ${d.delicate === true ? "!border-[var(--w-mint-ink)] bg-mint-soft" : ""}`}>
            <input type="radio" name="delicate" checked={d.delicate === true} onChange={() => set({ delicate: true })} data-testid="delicate-yes" /> Ja, meine Wäsche enthält empfindliche Textilien.
          </label>
        </div>
        {d.delicate === true && (
          <div className="space-y-3 pt-1">
            <div className="flex flex-wrap gap-2" data-testid="delicate-types">
              {DTYPES.map(([k, l]) => (
                <button type="button" key={k} onClick={() => toggleType(k)} aria-pressed={(d.delicate_types || []).includes(k)} data-testid={`delicate-type-${k}`}
                  className={`px-3 py-2 rounded-full text-sm border ${(d.delicate_types || []).includes(k) ? "bg-mint text-ink border-[var(--w-mint-ink)]" : "border-line"}`}>{l}</button>
              ))}
            </div>
            <label className="block"><span className="w-label">Pflegehinweise (optional)</span>
              <textarea className="w-input !h-20 py-3" value={d.delicate_notes} maxLength={1000} onChange={(e) => set({ delicate_notes: e.target.value })} placeholder="z. B. nur Handwäsche, nicht schleudern" data-testid="delicate-notes" /></label>
            <div>
              <label className="w-btn w-btn-secondary cursor-pointer inline-flex" data-testid="delicate-label-upload">
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={upBusy} onChange={(e) => uploadLabel(e.target.files?.[0])} />
                {upBusy ? "Lade…" : "Foto des Pflegeetiketts hochladen"}
              </label>
              {(d.delicate_label_photos || []).length > 0 && <span className="w-muted text-sm ml-3" data-testid="delicate-label-count">{d.delicate_label_photos.length} Foto(s) hinzugefügt</span>}
            </div>
          </div>
        )}
      </fieldset>
      {s.dryer && <Toggle checked={d.extras.dryer} onChange={(v) => setX({ dryer: v })} label={t("extra.dryer")} price={`${eur(DRYER)} / kg`} testId="extra-dryer" />}
      {s.folding && <Toggle checked={d.extras.folding} onChange={(v) => setX({ folding: v })} label={t("extra.folding")} price={`${eur(FOLD)} / kg`} testId="extra-folding" />}
      {["softener", "perfume", "sensitive"].map((k) => s[k] && <Toggle key={k} checked={!!d.extras[k]} onChange={(v) => setX({ [k]: v })} label={CONSUMABLE_LABELS[k]} price={`${eur(CONS)} / kg`} testId={`extra-${k}`} />)}
      {s.offers_heavy_soil !== false && (
        <div className="w-card p-4 space-y-3 border-amber-300" data-testid="heavy-soil-block">
          <Toggle checked={!!d.heavy_soil} onChange={(v) => set({ heavy_soil: v, heavy_soil_desc: v ? d.heavy_soil_desc : "" })} label="Stark verunreinigte Wäsche" price={`+ ${eur(pc.heavy_soil_price ?? 14.9)} / Auftrag`} testId="extra-heavy-soil" />
          <p className="w-muted text-sm">Bitte aktiviere diese Option, wenn deine Wäsche z. B. deutlich mit Blut, Urin, Stuhl oder anderen Körperausscheidungen verunreinigt ist. Dein Waschheld muss die Bearbeitung ausdrücklich akzeptieren. Stark verunreinigte Wäsche bitte getrennt verpackt übergeben.</p>
          {d.heavy_soil && (
            <label className="block">
              <span className="w-label">Art und Umfang der Verunreinigung (Pflicht)</span>
              <textarea className="w-input !h-24 py-3" value={d.heavy_soil_desc} maxLength={1000} onChange={(e) => set({ heavy_soil_desc: e.target.value })} placeholder="z. B. Zwei Bettlaken mit Urinflecken und eine Hose mit Stuhlverschmutzung." data-testid="heavy-soil-desc" />
              <span className="w-muted text-xs">Bitte keine Namen, Diagnosen oder sonstigen Gesundheitsdaten angeben. Bei infektiöser oder gefährlicher Kontamination ist eine Buchung über den regulären Washly-Service ausgeschlossen.</span>
            </label>
          )}
        </div>
      )}
      {s.ironing && (
        <div className="w-card p-4 space-y-3">
          <p className="font-bold">{t("extra.ironing")}</p>
          <Toggle checked={d.extras.iron_flat} onChange={(v) => setX({ iron_flat: v, pieces: [0, 0, 0] })} label={`${t("extra.iron_flat")} – ${t("book.iron_flat_desc")}`} price={eur(p.iron_flat_price)} testId="extra-iron-flat" />
          <div className={`rounded-2xl border border-line p-4 ${d.extras.iron_flat ? "opacity-50 pointer-events-none" : ""}`} data-testid="iron-pieces-block">
            <p className="font-semibold flex items-center gap-2 mb-3"><Shirt size={16} aria-hidden="true" /> Bügeln (je Stück) <span className="w-muted text-sm font-normal">— oder einzeln</span></p>
            <div className="space-y-3">
              {[["T-Shirt", pc.iron_tshirt_price ?? 1.49], ["Hemd / Bluse", pc.iron_shirt_price ?? 2.99], ["Hose", pc.iron_pants_price ?? 3.49]].map(([name, pr], i) => {
                const val = (d.extras.pieces || [0, 0, 0])[i] || 0;
                const setVal = (v) => { const pieces = [...(d.extras.pieces || [0, 0, 0])]; pieces[i] = Math.min(100, Math.max(0, v)); setX({ pieces }); };
                return (
                  <div key={name} className="flex items-center justify-between gap-4">
                    <span className="text-sm"><span className="block font-semibold">{name}</span><span className="w-muted">{eur(pr)}/Stück</span></span>
                    <div className="flex items-center rounded-full border border-line overflow-hidden" data-testid={`iron-piece-${i}`}>
                      <button type="button" className="px-4 py-2 text-lg hover:bg-gray-50 disabled:opacity-40" disabled={d.extras.iron_flat} onClick={() => setVal(val - 1)} data-testid={`iron-piece-${i}-minus`} aria-label={`${name} weniger`}>−</button>
                      <span className="px-4 min-w-[3rem] text-center font-semibold tabular-nums" data-testid={`iron-piece-${i}-value`}>{val}</span>
                      <button type="button" className="px-4 py-2 text-lg hover:bg-gray-50 disabled:opacity-40" disabled={d.extras.iron_flat} onClick={() => setVal(val + 1)} data-testid={`iron-piece-${i}-plus`} aria-label={`${name} mehr`}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <TransportStep d={d} set={set} p={p} quote={quote} />
    </div>
  );
}

function SlotStep({ d, set, pid }) {
  const { t } = useT();
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    setSlots(null);
    api.get(`/providers/${pid}/slots`, { params: { date: d.date } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [d.date, pid]);
  const chosen = slots?.find((s) => s.slot === d.slot);
  return (
    <div className="space-y-4">
      <label className="block"><span className="w-label">{t("book.date")}</span>
        <input type="date" className="w-input" min={tomorrow()} value={d.date} onChange={(e) => set({ date: e.target.value, slot: "" })} data-testid="booking-date-input" /></label>
      <fieldset><legend className="w-label">{t("book.slot")}</legend>
        {!slots ? <Loader /> : !slots.length ? <p className="w-muted" data-testid="no-slots">{t("book.noslots")}</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {slots.map((s) => (
              <button type="button" key={s.slot} aria-pressed={d.slot === s.slot} onClick={() => set({ slot: s.slot })} data-testid={`slot-${s.slot}`}
                className={`w-card p-3 font-semibold text-left ${d.slot === s.slot ? "!border-[var(--w-mint-ink)] bg-mint-soft" : ""}`}>
                {s.slot} Uhr {s.conflict && <span className="block text-sm text-[var(--w-warn)] font-semibold"><AlertTriangle size={14} className="inline" aria-hidden="true" /> ausgelastet</span>}
              </button>
            ))}
          </div>
        )}
      </fieldset>
      {chosen?.conflict && <p role="alert" className="w-card p-3 bg-[var(--w-warn-soft)] text-[var(--w-warn)] font-semibold" data-testid="slot-conflict-warning"><AlertTriangle size={16} className="inline mr-1" aria-hidden="true" />{t("book.conflict")}</p>}
    </div>
  );
}

function AddressStep({ d, set, user, pid }) {
  const { t } = useT();
  const nav = useNavigate();
  const a = d.address;
  const setA = (k) => (e) => set({ address: { ...a, [k]: e.target.value } });
  const lookup = async (plz) => {
    if (!/^\d{5}$/.test(plz)) return;
    try { const r = await api.get(`/geo/${plz}`); set({ address: { ...a, plz, city: a.city || r.data.place } }); } catch (e) { /* unknown plz */ }
  };
  if (!user) return (
    <div className="w-card p-6 space-y-4 bg-mint-soft" data-testid="booking-auth-gate">
      <p className="font-semibold">{t("book.login_needed")}</p>
      <div className="flex flex-wrap gap-3">
        <button type="button" className="w-btn w-btn-primary" onClick={() => nav(`/auth?mode=register&next=/book/${pid}?step=4`)} data-testid="booking-gate-register">{t("auth.register")}</button>
        <button type="button" className="w-btn w-btn-secondary" onClick={() => nav(`/auth?next=/book/${pid}?step=4`)} data-testid="booking-gate-login">{t("auth.login")}</button>
      </div>
    </div>
  );
  return (
    <div className="space-y-4">
      <p className="w-muted flex gap-2"><EyeOff size={18} className="mt-1 shrink-0" aria-hidden="true" />{t("book.address_privacy")}</p>
      <label className="block"><span className="w-label">{t("book.street")}</span><input className="w-input" value={a.street} onChange={setA("street")} autoComplete="street-address" data-testid="address-street-input" /></label>
      <div className="grid grid-cols-3 gap-3">
        <label className="block"><span className="w-label">{t("book.plz")}</span><input className="w-input" inputMode="numeric" maxLength={5} value={a.plz} onChange={(e) => { setA("plz")(e); lookup(e.target.value); }} autoComplete="postal-code" data-testid="address-plz-input" /></label>
        <label className="block col-span-2"><span className="w-label">{t("book.city")}</span><input className="w-input" value={a.city} onChange={setA("city")} data-testid="address-city-input" /></label>
      </div>
      <label className="block"><span className="w-label">{t("book.notes")}</span><input className="w-input" value={a.notes} onChange={setA("notes")} data-testid="address-notes-input" /></label>
    </div>
  );
}

function canNext(step, d, user) {
  if (step === 1) return d.kg >= 1 && d.kg <= 30;
  if (step === 2) return (d.delicate === true || d.delicate === false) && (!d.delicate || (d.delicate_types || []).length > 0) && (!d.heavy_soil || d.heavy_soil_desc.trim().length >= 10);
  if (step === 3) return !!d.slot;
  if (step === 4) return user && d.address.street.length >= 3 && /^\d{5}$/.test(d.address.plz) && d.address.city.length >= 2;
  return true;
}

export default function BookingWizard() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t } = useT();
  const { user } = useAuth();
  const nav = useNavigate();
  const key = `washly_draft_${id}`;
  const [p, setP] = useState(null);
  const [d, setD] = useState(() => loadDraft(key));
  const [step, setStep] = useState(() => Math.min(+params.get("step") || 1, 5));
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setD((x) => ({ ...x, ...patch }));
  const setX = (patch) => setD((x) => ({ ...x, extras: { ...x.extras, ...patch } }));

  useEffect(() => { api.get(`/providers/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  useEffect(() => { sessionStorage.setItem(key, JSON.stringify(d)); }, [d, key]);
  useEffect(() => {
    if (!d.kg || d.kg < 1) return;
    const plz = /^\d{5}$/.test(d.address.plz) ? d.address.plz : null;
    const h = setTimeout(() => api.post("/quote", { provider_id: id, kg: d.kg, extras: { ...d.extras, heavy_soil: d.heavy_soil }, plz }).then((r) => setQuote(r.data)).catch(() => {}), 200);
    return () => clearTimeout(h);
  }, [d.kg, d.extras, d.heavy_soil, d.address.plz, id]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { provider_id: id, ...d });
      sessionStorage.removeItem(key);
      toast.success(`${t("book.sent")} · ${data.code}`);
      nav(`/bookings/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!p) return <Loader />;
  if (user && user.role !== "customer" && step >= 4) return <div className="w-container py-10"><p className="w-card p-6">Buchungen sind nur mit einem Kundenkonto möglich.</p></div>;
  return (
    <div className="w-container py-8 grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8">
        <p className="w-muted">{p.display_name}</p>
        <div className="mb-3" data-testid="booking-insurance-badge"><InsuranceBadge insurance={p.insurance} testId="booking-insurance" /></div>
        <h1 className="text-3xl font-extrabold mb-4" data-testid="booking-step-title">{t(STEPS[step - 1])}</h1>
        <Stepper step={step} setStep={setStep} />
        <div className="w-card p-5 md:p-6 w-rise" key={step}>
          {step === 1 && (
            <div className="space-y-4">
              <label className="block"><span className="w-label">{t("book.kg")}</span>
                <div className="flex items-center gap-3">
                  <button type="button" className="w-btn w-btn-secondary !px-5" aria-label="weniger" onClick={() => set({ kg: Math.max(1, d.kg - 1) })} data-testid="kg-minus">−</button>
                  <input type="number" min="1" max="30" step="0.5" className="w-input !w-28 text-center text-2xl font-bold" value={d.kg} onChange={(e) => set({ kg: +e.target.value })} data-testid="kg-input" />
                  <button type="button" className="w-btn w-btn-secondary !px-5" aria-label="mehr" onClick={() => set({ kg: Math.min(30, d.kg + 1) })} data-testid="kg-plus">+</button>
                </div>
              </label>
              <p className="w-muted">{t("book.kg_hint")}</p>
            </div>
          )}
          {step === 2 && <ExtrasStep d={d} setX={setX} set={set} p={p} quote={quote} />}
          {step === 3 && <SlotStep d={d} set={set} pid={id} />}
          {step === 4 && <AddressStep d={d} set={set} user={user} pid={id} />}
          {step === 5 && (
            <div className="space-y-4" data-testid="booking-summary">
              <p><strong>{d.date}</strong> · {d.slot} Uhr · {t(`transport.${d.self_pickup ? "none" : d.extras.transport === true ? "both" : d.extras.transport || "none"}`)}</p>
              <p className="w-muted">{d.address.street}, {d.address.plz} {d.address.city}</p>
              <PriceBreakdown quote={quote} testId="summary-price" />
              {d.heavy_soil && <p className="w-card p-3 bg-[var(--w-warn-soft)] text-sm" data-testid="summary-heavy-soil"><strong>Stark verunreinigte Wäsche</strong> – dein Waschheld muss die Bearbeitung ausdrücklich bestätigen. Deine Angabe: „{d.heavy_soil_desc}"</p>}
              {d.delicate && <p className="w-card p-3 bg-mint-soft text-sm" data-testid="summary-delicate"><strong>Empfindliche Textilien:</strong> {(d.delicate_types || []).map((k) => ({ seide: "Seide", kaschmir: "Kaschmir", wolle: "Wolle", andere: "Andere" }[k])).join(", ")}{d.delicate_notes ? ` · Hinweise: ${d.delicate_notes}` : ""}. Dein Waschheld muss die fachgerechte Behandlung ausdrücklich bestätigen.</p>}
              <p className="w-muted flex gap-2"><Check size={18} className="mt-1 text-mint-ink" aria-hidden="true" />{t("book.pay_after")}</p>
            </div>
          )}
        </div>
        <div className="flex justify-between mt-4">
          <button className="w-btn w-btn-secondary" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="booking-back-btn">{t("book.back")}</button>
          {step < 5 ? (
            <button className="w-btn w-btn-primary" disabled={!canNext(step, d, user)} onClick={() => setStep(step + 1)} data-testid="booking-next-btn">{t("book.next")}</button>
          ) : (
            <button className="w-btn w-btn-primary" disabled={busy || !user} onClick={submit} data-testid="booking-submit-btn">{busy ? t("common.loading") : t("book.submit")}</button>
          )}
        </div>
      </div>
      <aside className="lg:col-span-4">
        <div className="w-card p-5 lg:sticky lg:top-20" aria-live="polite"><h2 className="font-bold mb-3">Preis</h2>{quote ? <PriceBreakdown quote={quote} testId="sidebar-price" /> : <Loader />}</div>
      </aside>
    </div>
  );
}
